"use client";

/**
 * LiquidBubble — círculo líquido grande para nodos focales interactivos.
 *
 * Composite de `LiquidSurface` (igual contrato que `LiquidPill`):
 * same props de superficie, diameter vía `size` semántico — sin recrear
 * vidrio con Tailwind ni `!important` sobre CIRCLE_SIZES del pill.
 *
 *   LiquidSurface  ← material
 *     ↑
 *   LiquidBubble   ← círculo grande + layout centrado
 */

import * as React from "react";
import { cn } from "@/lib/utils";
import { type CircleActionVariant } from "./circle-action-variants";
import {
  LiquidSurface,
  type LiquidDeformAxis,
  type LiquidSurfaceBounceIntensity,
  type LiquidSurfaceShadow,
  type LiquidSurfaceStyle,
} from "./liquid-surface";
import type { LiquidPillAppearance } from "./liquid-pill";

export type LiquidBubbleSize = "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "hero";

export interface LiquidBubbleProps {
  variant?: CircleActionVariant;
  appearance?: LiquidPillAppearance;
  size?: LiquidBubbleSize;
  /** Diámetro en px; si se pasa, gana sobre `size`. */
  diameter?: number;
  icon?: React.ReactNode;
  text?: React.ReactNode;
  children?: React.ReactNode;
  as?: "div" | "button" | "span";
  type?: "button" | "submit" | "reset";
  onClick?: React.MouseEventHandler<HTMLElement>;
  disabled?: boolean;
  active?: boolean;
  surfaceStyle?: LiquidSurfaceStyle;
  interactive?: boolean;
  shadow?: LiquidSurfaceShadow;
  cursorGlow?: boolean;
  cursorGlowVisible?: boolean;
  reactive?: boolean;
  hoverBounce?: boolean;
  hoverDeform?: boolean;
  bounceIntensity?: LiquidSurfaceBounceIntensity;
  deformAxis?: LiquidDeformAxis;
  "aria-label"?: string;
  id?: string;
}

const BUBBLE_DIAMETER: Record<LiquidBubbleSize, number> = {
  md: 36,
  lg: 44,
  xl: 56,
  "2xl": 64,
  "3xl": 96,
  "4xl": 140,
  "5xl": 180,
  hero: 240,
};

export function liquidBubbleDiameter(
  size: LiquidBubbleSize = "hero",
  diameter?: number,
): number {
  return diameter ?? BUBBLE_DIAMETER[size];
}

export const LiquidBubble = React.forwardRef<
  HTMLButtonElement | HTMLDivElement | HTMLSpanElement,
  LiquidBubbleProps
>(function LiquidBubble(
  {
    variant,
    appearance = "tinted",
    size = "hero",
    diameter,
    icon,
    text,
    children,
    as = "button",
    type = "button",
    onClick,
    disabled,
    active = false,
    surfaceStyle = "sidebarInactive",
    interactive = false,
    shadow = "neutral",
    cursorGlow = true,
    cursorGlowVisible = false,
    reactive = true,
    hoverBounce = false,
    hoverDeform = true,
    bounceIntensity = "soft",
    deformAxis = "both",
    id,
    "aria-label": ariaLabel,
  },
  ref,
) {
  const px = liquidBubbleDiameter(size, diameter);
  const tone: "neutral" | "tinted" =
    appearance === "tinted" || active ? "tinted" : "neutral";
  const isInteractive = interactive || as === "button" || Boolean(onClick);

  const inner =
    children ??
    (icon != null || text != null ? (
      <span className="relative z-[3] flex max-w-[85%] flex-col items-center justify-center gap-2 text-center">
        {icon != null && (
          <span className="flex shrink-0 items-center leading-none">{icon}</span>
        )}
        {text != null && (
          <span className="text-sm font-semibold tracking-[-0.01em] md:text-base">
            {text}
          </span>
        )}
      </span>
    ) : null);

  return (
    <LiquidSurface
      ref={ref as React.Ref<HTMLElement>}
      variant={variant}
      tone={tone}
      shape="circle"
      size="free"
      as={as}
      interactive={isInteractive}
      shadow={shadow}
      surfaceStyle={surfaceStyle}
      cursorGlow={cursorGlow}
      cursorGlowVisible={cursorGlowVisible}
      reactive={reactive}
      hoverBounce={hoverBounce}
      hoverDeform={hoverDeform}
      bounceIntensity={bounceIntensity}
      deformAxis={deformAxis}
      onClick={onClick}
      type={type}
      disabled={disabled}
      id={id}
      aria-label={ariaLabel}
      className={cn(
        "liquid-bubble liquid-pill inline-flex shrink-0 items-center justify-center select-none",
      )}
      style={{
        width: px,
        height: px,
        minWidth: px,
        minHeight: px,
      }}
    >
      {inner}
    </LiquidSurface>
  );
});

LiquidBubble.displayName = "LiquidBubble";

export { BUBBLE_DIAMETER };
